import axiosInstance from './axiosInstance';

const BASE = '/admin/study-materials';

export function genId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeCourse(course) {
  return { ...course, tree: course.tree || [] };
}

export async function loadCourses() {
  const { data } = await axiosInstance.get(`${BASE}/courses`);
  return data;
}

export async function getCourse(courseId) {
  try {
    const { data } = await axiosInstance.get(`${BASE}/courses/${courseId}`);
    return normalizeCourse(data);
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

export async function createCourse({ title, description }) {
  const { data } = await axiosInstance.post(`${BASE}/courses`, { title, description });
  return normalizeCourse(data);
}

// patch may contain course fields (title/description/status) and/or a full
// `tree` replacement — whichever keys are present get persisted, then the
// authoritative course (including any server-assigned ids) is returned so
// callers can just setCourse(updated).
export async function updateCourse(courseId, patch) {
  const { tree, ...fields } = patch;
  if (tree !== undefined) {
    await axiosInstance.put(`${BASE}/courses/${courseId}/tree`, { tree });
  }
  if (Object.keys(fields).length > 0) {
    await axiosInstance.put(`${BASE}/courses/${courseId}`, fields);
  }
  const { data } = await axiosInstance.get(`${BASE}/courses/${courseId}`);
  return normalizeCourse(data);
}

export async function deleteCourse(courseId) {
  await axiosInstance.delete(`${BASE}/courses/${courseId}`);
}

// ── Batch sharing ────────────────────────────────────────────────────────────
export async function getCourseBatches(courseId) {
  const { data } = await axiosInstance.get(`${BASE}/courses/${courseId}/batches`);
  return data;
}

export async function addCourseBatch(courseId, batchId) {
  await axiosInstance.post(`${BASE}/courses/${courseId}/batches`, { batchId });
}

export async function removeCourseBatch(courseId, batchId) {
  await axiosInstance.delete(`${BASE}/courses/${courseId}/batches/${batchId}`);
}

// ── Staff sharing ────────────────────────────────────────────────────────────
export async function getCourseStaff(courseId) {
  const { data } = await axiosInstance.get(`${BASE}/courses/${courseId}/staff`);
  return data;
}

export async function addCourseStaff(courseId, staffId) {
  await axiosInstance.post(`${BASE}/courses/${courseId}/staff`, { staffId });
}

export async function removeCourseStaff(courseId, staffId) {
  await axiosInstance.delete(`${BASE}/courses/${courseId}/staff/${staffId}`);
}

// ── Tree helpers (Category > Main Title > Sub Title) ───────────────────────
export function newCategory(name) {
  return { id: genId(), name: name.trim(), mainTitles: [] };
}

export function newMainTitle(title) {
  return { id: genId(), title: title.trim(), subTitles: [] };
}

export function newSubTitle(title) {
  return {
    id: genId(),
    title: title.trim(),
    status: 'draft', // 'draft' | 'published'
    hidden: false,
    content: { en: '', ta: '' },
  };
}

// Counts every sub-title (leaf/lesson) across the whole tree.
export function countLessons(tree) {
  return (tree || []).reduce(
    (sum, cat) => sum + (cat.mainTitles || []).reduce((s2, mt) => s2 + (mt.subTitles || []).length, 0),
    0
  );
}
